import React, {ReactElement, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {AnyAction, bindActionCreators, Dispatch} from 'redux';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlug} from '@fortawesome/free-solid-svg-icons';
import {formatMessage} from 'i18n/CurrentLocale';
import {FormattedMessage} from 'react-intl';
import {Api} from 'api';
import {ModalEnum} from 'myParkdUtils/enums';
import {CONTENT_CONNECTION_REQUESTED_MODAL} from 'myParkdUtils/basicMessageModalContent';
import {ReduxState, TelematicProviderType, TelematicProviderTypeMetadata} from 'types';
import {getAllTelematicTypeMetadata} from "../../features/company/companyDetails";
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as companyActions from 'features/company/companyActions';
import ErrorMessageBox from 'components/ErrorMessageBox';
import placeholders from 'string-translations/placeholders';
import Select from 'react-select';
import SubmitButton from 'components/SubmitButton';


export type DropdownOption = {
    value: string;
    label: string | ReactElement<any>;
};

export const ChooseConnectionModal = (props: Props) => {
    const [connectWith, setConnectWith] = useState<string | null>(null);
    const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
    const [hasConnectedFleet, setHasConnectedFleet] = useState<boolean>(true);
    const [connectionRequestStatus, setConnectionRequestStatus] = useState({ loading: false, loaded: false, error: false });
    const [noProviderSelectedError, setNoProviderSelectedError] = useState<boolean>(false);
    const [otherTelematicAccountName, setOtherTelematicAccountName] = useState<string>('');

    useEffect(() => {
        if (!props.telematicProviderTypeMetadata?.length) {
            props.getTelematicAccountTypes();
        }
    }, []);

    useEffect(() => {
        if (props.telematicProviderTypeMetadata?.length) {
            prepareDropdownOptions();
        }
    }, [props.telematicProviderTypeMetadata]);

    const prepareDropdownOptions = async () => {
        const options = createOptionsForTelematicAccountDropdown(props.telematicProviderTypeMetadata);
        setDropdownOptions(options);
    };

    const createOptionsForTelematicAccountDropdown = (supportedTelematicAccounts: TelematicProviderTypeMetadata[]): DropdownOption[] => {
        let options = [] as DropdownOption[];
        options = addSupportedTelematicProviders(options, supportedTelematicAccounts);
        options = addOtherTelematicProviderOption(options);
        return options;
    };

    const addSupportedTelematicProviders = (options: DropdownOption[], supportedTelematicAccounts: TelematicProviderTypeMetadata[]) => {
        supportedTelematicAccounts.forEach((account, i: number) => {
            if (account.providerType !== 'APP') {
                const accountDisplayName =
                    account.displayName +
                    (isProviderTypeAlreadyConnected(account.providerType)
                        ? ' ' + formatMessage({ id: 'CONNECT_WITH_CONNECTED_STATUS_IN_DROPDOWN' })
                        : '');
                options.push({
                    value: account.providerType,
                    label: <span className={'connection-option ' + account.providerType}>{accountDisplayName}</span>,
                });
            }
        });

        return options
    };

    const addOtherTelematicProviderOption = (options: DropdownOption[]) => {
        options.push({
            value: 'OTHER',
            label: (
                <span className="connection-option OTHER">
                    <FormattedMessage id="OTHER" />
                </span>
            ),
        });
        return options;
    };

    const isProviderTypeAlreadyConnected = (providerType: TelematicProviderType): boolean => {
        return props.companyTelematicAccounts
            .some(telematicProviderType => telematicProviderType.providerType === providerType)
    };

    const handleSelectChange = (connectWith: DropdownOption) => {
        setConnectWith(connectWith.value);
        setNoProviderSelectedError(false);
    };

    const onSubmit = async (e: any) => {
        e.preventDefault();
        if ((hasConnectedFleet && connectWith) || !hasConnectedFleet) {
            const message =
                'MY_PARKD_TELEMATIC_CONNECTION_REQUEST' +
                '\n' +
                'telematicProvider: ' +
                (hasConnectedFleet ? connectWith : 'PARK_MANUALLY_WITH_PARKD_APP_ONLY') +
                (connectWith === 'OTHER' ? ' -> ' + otherTelematicAccountName : '');
            try {
                setConnectionRequestStatus({ loading: true, loaded: false, error: false });
                await Api.sendMessageToSupport(props.token!, { description: message });
                props.updateModal(ModalEnum.BASIC_MESSAGE_MODAL, {
                    modalContent: CONTENT_CONNECTION_REQUESTED_MODAL,
                });
                setConnectionRequestStatus({ loading: false, loaded: true, error: false });
            } catch (err) {
                console.log(err);
                props.updateModal(ModalEnum.SOMETHING_WENT_WRONG);
                setConnectionRequestStatus({ loading: false, loaded: false, error: true });
            }
        } else {
            setNoProviderSelectedError(true);
        }
    };

    return (
        <div id="choose-connection-modal" className="panel radius-rectangle full-width-form">
            <div className="panel-header">
                <h1 className="bold-title">
                    <FontAwesomeIcon icon={faPlug} />
                    <FormattedMessage id="REGISTRATION_HOWWILLYOUCONNECT_TITLE" />
                </h1>
                <button className="btn-close-modal" onClick={() => props.closeModal()} />
            </div>
            <div className="panel-content">
                <form onSubmit={onSubmit} className={connectWith ? 'validation-successful' : ''}>
                    {noProviderSelectedError && (
                        <ErrorMessageBox errorMessage={formatMessage({ id: 'ERROR_CHOOSE_A_TRACKANDTRACE_FROM_THE_LIST' })} />
                    )}

                    {hasConnectedFleet ? (
                        <React.Fragment>
                            <label htmlFor="telematic-provider-dropdown" className="align-left">
                                <FormattedMessage id="CONNECT_WITH_CHOOSE_YOUR_TELEMATIC_PROVIDER" />
                            </label>
                            <Select
                                //data-testid="d"
                                id="telematic-provider-dropdown"
                                name="connection-dropdown"
                                value={dropdownOptions.filter(({ value }) => value === connectWith)} // V2 - simpleValue
                                placeholder={formatMessage(placeholders.chooseService)}
                                onChange={(e: any) => handleSelectChange(e)}
                                options={dropdownOptions}
                                isOptionDisabled={(option) => isProviderTypeAlreadyConnected(option.value)}
                                isLoading={props.telematicTypeMetadataStatus?.loading}
                                isSearchable={false}
                                className={'react-select-container react-select-with-border'}
                                classNamePrefix={'react-select'}
                            />

                            {connectWith === 'OTHER' && (
                                <React.Fragment>
                                    <label htmlFor="other-telematic-provider-name" className="align-left">
                                        <FormattedMessage id="CONNECT_WITH_NAME_OF_YOUR_TELEMATIC_PROVIDER" />
                                    </label>
                                    <input
                                        type="text"
                                        id="other-telematic-provider-name"
                                        className="text-field"
                                        onChange={(e) => setOtherTelematicAccountName(e.target.value)}
                                        placeholder="Webfleet Solutions"
                                    />
                                </React.Fragment>
                            )}

                            <hr className="hr" />

                            <SubmitButton
                                buttonText={formatMessage({ id: 'SEND_CONNECTION_REQUEST' })}
                                hasArrow={true}
                                saving={connectionRequestStatus.loading}
                            />
                            <span className="fake-link small-gray-link" onClick={() => setHasConnectedFleet(!hasConnectedFleet)}>
                                <FormattedMessage id="LINK_MY_FLEET_IS_NOT_CONNECTED" />
                            </span>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <p className="align-left">
                                <FormattedMessage id="CONNECT_WITH_USE_PARKD_WITHOUT_TELEMATIC_PROVIDER" />
                            </p>
                            <hr className="hr" />
                            <SubmitButton
                                buttonText={formatMessage({ id: 'CONNECT_WITHOUT_TRACK_AND_TRACE' })}
                                hasArrow={true}
                                saving={connectionRequestStatus.loading}
                            />
                            <span className="fake-link small-gray-link" onClick={() => setHasConnectedFleet(!hasConnectedFleet)}>
                                <FormattedMessage id="LINK_BACK_TO_CONNECT_WITH_PARKD" />
                            </span>
                        </React.Fragment>
                    )}
                </form>
            </div>
        </div>
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = DispatchProps & StateProps;

function mapStateToProps(state: ReduxState) {
    return {
        token: state.auth.token,
        companyTelematicAccounts: state.companyTelematicAccounts.accounts,
        telematicProviderTypeMetadata: getAllTelematicTypeMetadata(state),
        telematicTypeMetadataStatus: state.companyDetails.telematicTypeMetadataStatus
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        getTelematicAccountTypes: companyActions.getTelematicAccountTypes,
        updateModal: modalWindowActions.updateModalWindow,
        closeModal: modalWindowActions.closeModal
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ChooseConnectionModal);
